import { io } from "socket.io-client";
import { SOCKET_URL } from "../../../config";

const API_BASE_URL = SOCKET_URL;

console.log('[ChatSocket] Creating socket connection to:', `${API_BASE_URL}/tickets`);

const socket = io(`${API_BASE_URL}/tickets`, {
  transports: ['websocket'],
  timeout: 10000
});

socket.on('connect', () => {
  console.log('[ChatSocket] Connected to tickets namespace, socket id:', socket.id);
});

socket.on('connect_error', (error) => {
  console.error('[ChatSocket] Connection error:', error.message);
});

socket.on('disconnect', (reason) => {
  console.warn('[ChatSocket] Disconnected:', reason);
});

/**
 * Create a ticket using the /tickets namespace. This uses a short-lived socket connection
 * so it doesn't interfere with the main support socket.
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
const createTicket = (payload) => {
  return new Promise((resolve, reject) => {
    if (!payload) return reject(new Error('Missing ticket payload'));

    const temp = io(`${API_BASE_URL}/tickets`, { transports: ['websocket'], timeout: 10000 });

    const cleanup = () => {
      try { temp.disconnect(); } catch (e) {}
    };

    temp.on('connect', () => {
      temp.emit('tickets:create', payload, (resp) => {
        cleanup();
        if (resp && resp.success) resolve(resp);
        else reject(new Error(resp?.error || 'Failed to create ticket'));
      });
    });

    temp.on('connect_error', (err) => {
      cleanup();
      reject(err || new Error('Connection error to tickets namespace'));
    });
  });
};

const startSupportChat = (chatData) => {
  return new Promise((resolve, reject) => {
    if (!chatData) {
      reject(new Error('No chat data provided'));
      return;
    }

    console.log('[ChatSocket] Starting support chat with data:', chatData);
    console.log('[ChatSocket] Socket connected:', socket.connected);
    
    socket.emit('support:staff:start', chatData, (response) => {
      console.log('[ChatSocket] support:staff:start response:', response);
      if (response && response.success) {
        resolve(response);
      } else {
        reject(new Error(response?.error || 'Failed to start chat'));
      }
    });
  });
};

const joinSupportTicket = (ticketId, user = {}) => {
  return new Promise((resolve, reject) => {
    if (!ticketId) {
      reject(new Error('Missing ticketId'));
      return;
    }

    console.log('[ChatSocket] Joining support ticket:', ticketId, user);
    
    socket.emit(
      'support:staff:join',
      {
        ticketId,
        userId: user.user_id || user.id,
        user_name: user.user_name || user.name || user.user_full_name,
        user_email: user.user_email || user.email
      },
      (response) => {
        console.log('[ChatSocket] support:staff:join response:', response);
        if (!response || response.success === false) {
          reject(new Error(response?.error || 'Failed to join chat'));
        } else {
          resolve(response);
        }
      }
    );
  });
};

const fetchSupportHistory = (ticketId, options = {}) => {
  return new Promise((resolve, reject) => {
    if (!ticketId) {
      reject(new Error('Missing ticketId'));
      return;
    }

    socket.emit(
      'support:ticket:history',
      {
        ticketId,
        markRead: options.markRead ?? false
      },
      (response) => {
        if (!response || response.success === false) {
          reject(new Error(response?.error || 'Failed to load history'));
        } else {
          resolve(response);
        }
      }
    );
  });
};

const sendSupportMessage = (message) => {
  return new Promise((resolve, reject) => {
    if (!message) {
      reject(new Error('No message provided'));
      return;
    }

    const payload = { ...message };

    if (payload.content && !payload.message) {
      payload.message = payload.content;
    }

    if (!payload.timestamp) {
      payload.timestamp = Math.floor(Date.now() / 1000);
    }

    socket.emit('support:message:send', payload, (response) => {
      if (!response || response.success === false) {
        reject(new Error(response?.error || 'Failed to send message'));
      } else {
        resolve(response);
      }
    });
  });
};

const closeSupportTicket = (ticketId, reason) => {
  return new Promise((resolve, reject) => {
    if (!ticketId) {
      reject(new Error('Missing ticketId'));
      return;
    }

    socket.emit(
      'support:ticket:close',
      {
        ticketId,
        reason
      },
      (response) => {
        if (!response || response.success === false) {
          reject(new Error(response?.error || 'Failed to close chat'));
        } else {
          resolve(response);
        }
      }
    );
  });
};

export {
  socket as default,
  startSupportChat,
  joinSupportTicket,
  fetchSupportHistory,
  sendSupportMessage,
  closeSupportTicket
  ,createTicket
};
